import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IInvite } from '../../model/invites.model';
import { IMember, IPermission } from '../../model/members.model';
import { IOrganization } from '../../model/organization.model';
import { ISibasiHTTPResponse } from '../../model/sibasiHTTPResponse.model';
import { IUser, UserRoles } from '../../model/user.model';
import { IPagination } from '../../model/util.model';

@Injectable({
  providedIn: 'root'
})
export class MemberService {
  private memberInvited = new BehaviorSubject<boolean>(false);
  readonly memberInvited$ = this.memberInvited.asObservable();
  pinnedInMemberPermissions: string[] | undefined = [];
  POSMember_Key = 'POSMember';
  member: IMember | undefined;

  readonly httpClient = inject(HttpClient);

  public getAllMembersAdmin(): Observable<ISibasiHTTPResponse<IMember[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IMember[]>>(
      `${environment.api_url}organizationadmin/members`
    );
  }

  public getAllMembers(): Observable<ISibasiHTTPResponse<IMember[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IMember[]>>(
      `${environment.api_url}organization/members/all`
    );
  }

  public getMemberById(memberId: string): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.get<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organization/members/one/${memberId}`
    );
  }

  getAllMembersCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}organization/members/count/all`
    );
  }

  getAllSearchedMembersCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}organization/members/count/searched`,
      { searchParam }
    );
  }

  getPaginatedMembers(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<IMember[]>>> {
    return this.httpClient.post<ISibasiHTTPResponse<IPagination<IMember[]>>>(
      `${environment.api_url}organization/members/paginated`,
      pagination ? pagination : {}
    );
  }
  public deleteMemberById(memberId: any) {
    return this.httpClient.delete(
      `${environment.api_url}organization/members/remove/${memberId}`
    );
  }
  public getAllMembersPendingInvites(): Observable<ISibasiHTTPResponse<IInvite[]>> {
    return this.httpClient.get<ISibasiHTTPResponse<IInvite[]>>(
      `${environment.api_url}organization/members/invites/unaccepted`
    );
  }
  getAllPendingMembersCount(): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.get<ISibasiHTTPResponse<number>>(
      `${environment.api_url}organization/members/invites/unaccepted/count/all`
    );
  }

  getAllPendingSearchedMembersCount(
    searchParam: string
  ): Observable<ISibasiHTTPResponse<number>> {
    return this.httpClient.post<ISibasiHTTPResponse<number>>(
      `${environment.api_url}organization/members/invites/unaccepted/count/searched`,
      { searchParam }
    );
  }

  getPendingPaginatedInvites(
    pagination?: any
  ): Observable<ISibasiHTTPResponse<IPagination<IMember[]>>> {
    return this.httpClient
      .post<
        ISibasiHTTPResponse<IPagination<IMember[]>>
      >(`${environment.api_url}organization/members/invites/unaccepted/paginated`, pagination || {})
      .pipe(
        map((res) => {
          const reversedData =
            res.result?.data?.length > 0 ? res.result.data.slice().reverse() : [];

          return {
            ...res,
            result: {
              ...res.result, // Preserve pagination info like `totalItems` if it exists
              data: reversedData
            }
          };
        })
      );
  }

  updatedMemberInvite(
    invite: IMember,
    role: string
  ): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.post<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organizationadmin/inviteRole/${invite._id}`,
      { assignedRole: role }
    );
  }

  public getMemberRole(
    organizationId: string
  ): Observable<ISibasiHTTPResponse<IOrganization>> {
    return this.httpClient.get<ISibasiHTTPResponse<IOrganization>>(
      `${environment.api_url}organization/check/ownership/roles/${organizationId}`
    );
  }
  public getInvitedMemberById(inviteId: any): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.get<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organizationadmin/invites/${inviteId}`
    );
  }

  public inviteMember(member: any): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient
      .post<
        ISibasiHTTPResponse<IMember>
      >(`${environment.api_url}organizationadmin/invites`, member)
      .pipe(
        map((res) => {
          this.memberInvited.next(true);
          return res;
        })
      );
  }

  public deleteInvitedMemberById(memberId: any) {
    return this.httpClient.delete(
      `${environment.api_url}organizationadmin/invites/${memberId}`
    );
  }

  public editMember(
    memberId: any,
    member: unknown
  ): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.post<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organizationadmin/invites/${memberId}`,
      member
    );
  }
  // assignMemberRole(
  //     role: { role: UserRoles },
  //     memberId: string
  // ): Observable<ISibasiHTTPResponse<IMember>> {
  //     return this.http.post<ISibasiHTTPResponse<IMember>>(
  //         `${environment.api_url}organizationadmin/assignRole/${memberId}`,
  //         role
  //     );
  // }
  assignWorkspaceMemberRole(
    role: IPermission,
    memberId: string,
    workspaceId: string
  ): Observable<ISibasiHTTPResponse<IUser>> {
    return this.httpClient.get<ISibasiHTTPResponse<IUser>>(
      `${environment.api_url}sibasi-workspace/roles/assign/${role?._id}/${memberId}/${workspaceId}`
    );
  }

  assignMemberRole(
    role: IPermission,
    memberId: string
  ): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.get<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organization/member/roles/assign/${role?._id}/${memberId}`
    );
  }

  unAssignMemberRole(
    role: { role: UserRoles },
    memberId: string
  ): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.post<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organizationadmin/removeRole/${memberId}`,
      role
    );
  }
  manageMemberRoles(
    roles: unknown,
    memberId: string
  ): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.post<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organizationadmin/edit/member/roles/${memberId}`,
      roles
    );
  }

  /******************************* NEW SERVICES *******************************/

  getAllMembersPagination(
    paginationParams: IPagination<IMember[]>
  ): Observable<ISibasiHTTPResponse<IPagination<IMember[]>>> {
    return this.httpClient.post<ISibasiHTTPResponse<IPagination<IMember[]>>>(
      `${environment.api_url}monival/client/project/paginated`,
      paginationParams
    );
  }

  getPOSLoggedInMember(): IMember | null {
    const stringMember = localStorage.getItem(this.POSMember_Key);
    if (stringMember) {
      const member: IMember = JSON.parse(stringMember);
      return member;
    }
    return null;
  }
  setMemberPin(
    memberId: string | undefined,
    pin: any
  ): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}organization/members/pin/reset/${memberId}`,
      pin
    );
  }

  authorizeMemberPin(pin: string): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.get<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organization/members/desktop/authorize/${pin}`
    );
  }

  verifyAdminPassword(password: string): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.post<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}user/validatePassword`,
      { password }
    );
  }

  deleteLoggedInMember(): void {
    localStorage.removeItem(this.POSMember_Key);
  }

  resendInvite(member: IMember): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.post<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organizationadmin/invites/resend`,
      member
    );
  }

  public async checkIfMemberHasRole(roleName: string): Promise<boolean> {
    if (!this.member) {
      const response = await firstValueFrom(
        this.httpClient.get<ISibasiHTTPResponse<IMember>>(
          `${environment.api_url}organization/members/info/one`
        )
      );
      this.member = response.result;
    }
    if (this.member?.assignedRole?.roleName.toUpperCase() === roleName.toUpperCase()) {
      return true;
    }
    return false;
  }

  public getCurrentLoggedInMember(): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.get<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}organization/members/info/one`
    );
  }

  public toggleTeamsCalendarSync(
    synchTeamsCalendar: boolean
  ): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.post<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}sync/teams`,
      { synchTeamsCalendar }
    );
  }

  public toggleGoogleCalendarSync(
    synchGoogleCalendar: boolean
  ): Observable<ISibasiHTTPResponse<IMember>> {
    return this.httpClient.post<ISibasiHTTPResponse<IMember>>(
      `${environment.api_url}sync/google`,
      { synchGoogleCalendar }
    );
  }
}
